/**
 * Front desk — the call and walk-in log, callbacks and contact history — over
 * gov-api.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ContactAssignee,
  ContactFileLookup,
  ContactLogEntryView,
  ContactLogList,
  CreateContactLogDto,
  FileContactHistory,
  FrontDeskStatus,
  MyCallbacks,
  UpdateContactLogDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';

export const frontDeskKeys = {
  all: ['front-desk'] as const,
  status: ['front-desk', 'status'] as const,
  entries: (localGovernmentId: string | null, q: string) =>
    ['front-desk', 'entries', localGovernmentId ?? 'all', q] as const,
  entry: (id: string) => ['front-desk', 'entry', id] as const,
  mine: ['front-desk', 'callbacks', 'mine'] as const,
  lookup: (localGovernmentId: string | null, q: string) =>
    ['front-desk', 'lookup', localGovernmentId ?? 'all', q] as const,
  assignees: (localGovernmentId: string) => ['front-desk', 'assignees', localGovernmentId] as const,
  fileHistory: (fileId: string) => ['front-desk', 'file', fileId] as const,
};

export async function getFrontDeskStatus(): Promise<FrontDeskStatus> {
  const { data } = await api.get<FrontDeskStatus>('/front-desk/status');
  return data;
}

export async function listContactEntries(
  localGovernmentId: string | null,
  q: string,
): Promise<ContactLogList> {
  const { data } = await api.get<ContactLogList>('/front-desk/entries', {
    params: {
      ...(localGovernmentId ? { localGovernmentId } : {}),
      ...(q.trim() ? { q: q.trim() } : {}),
    },
  });
  return data;
}

export async function getContactEntry(id: string): Promise<ContactLogEntryView> {
  const { data } = await api.get<ContactLogEntryView>(`/front-desk/entries/${id}`);
  return data;
}

export async function createContactEntry(dto: CreateContactLogDto): Promise<ContactLogEntryView> {
  const { data } = await api.post<ContactLogEntryView>('/front-desk/entries', dto);
  return data;
}

export async function updateContactEntry(
  id: string,
  dto: UpdateContactLogDto,
): Promise<ContactLogEntryView> {
  const { data } = await api.patch<ContactLogEntryView>(`/front-desk/entries/${id}`, dto);
  return data;
}

export async function attachContactToFile(
  id: string,
  fileId: string,
): Promise<ContactLogEntryView> {
  const { data } = await api.post<ContactLogEntryView>(`/front-desk/entries/${id}/files`, {
    fileId,
  });
  return data;
}

export async function recordOpenedFile(id: string, fileId: string): Promise<ContactLogEntryView> {
  const { data } = await api.post<ContactLogEntryView>(`/front-desk/entries/${id}/opened-file`, {
    fileId,
  });
  return data;
}

export async function sendContactToTriage(
  id: string,
  description?: string,
): Promise<ContactLogEntryView> {
  const { data } = await api.post<ContactLogEntryView>(
    `/front-desk/entries/${id}/triage`,
    description ? { description } : {},
  );
  return data;
}

export async function setContactCallback(
  id: string,
  assigneeId: string,
  dueAt: string,
): Promise<ContactLogEntryView> {
  const { data } = await api.put<ContactLogEntryView>(`/front-desk/entries/${id}/callback`, {
    assigneeId,
    dueAt,
  });
  return data;
}

export async function completeContactCallback(
  id: string,
  note?: string,
): Promise<ContactLogEntryView> {
  const { data } = await api.post<ContactLogEntryView>(
    `/front-desk/entries/${id}/callback/done`,
    note ? { note } : {},
  );
  return data;
}

export async function getMyCallbacks(): Promise<MyCallbacks> {
  const { data } = await api.get<MyCallbacks>('/front-desk/callbacks/mine');
  return data;
}

export async function lookupContactFiles(
  localGovernmentId: string | null,
  q: string,
): Promise<ContactFileLookup> {
  const { data } = await api.get<ContactFileLookup>('/front-desk/file-lookup', {
    params: { q, ...(localGovernmentId ? { localGovernmentId } : {}) },
  });
  return data;
}

export async function getContactAssignees(localGovernmentId: string): Promise<ContactAssignee[]> {
  const { data } = await api.get<ContactAssignee[]>('/front-desk/assignees', {
    params: { localGovernmentId },
  });
  return data;
}

export async function getFileContactHistory(fileId: string): Promise<FileContactHistory> {
  const { data } = await api.get<FileContactHistory>(`/files/${fileId}/contact-history`);
  return data;
}

// ── hooks ─────────────────────────────────────────────────────────────────

export function useFrontDeskStatus() {
  return useQuery({ queryKey: frontDeskKeys.status, queryFn: getFrontDeskStatus });
}

export function useContactEntries(localGovernmentId: string | null, q: string, enabled = true) {
  return useQuery({
    queryKey: frontDeskKeys.entries(localGovernmentId, q),
    queryFn: () => listContactEntries(localGovernmentId, q),
    enabled,
  });
}

/** One entry, e.g. to prefill intake. Off until an id is known. */
export function useContactEntry(id: string | null | undefined) {
  return useQuery({
    queryKey: frontDeskKeys.entry(id ?? ''),
    queryFn: () => getContactEntry(id as string),
    enabled: Boolean(id),
  });
}

export function useMyCallbacks(enabled = true) {
  return useQuery({ queryKey: frontDeskKeys.mine, queryFn: getMyCallbacks, enabled });
}

export function useContactFileLookup(localGovernmentId: string | null, q: string) {
  const term = q.trim();
  return useQuery({
    queryKey: frontDeskKeys.lookup(localGovernmentId, term),
    queryFn: () => lookupContactFiles(localGovernmentId, term),
    enabled: term.length >= 2,
    staleTime: 30_000,
  });
}

export function useContactAssignees(localGovernmentId: string | null) {
  return useQuery({
    queryKey: frontDeskKeys.assignees(localGovernmentId ?? ''),
    queryFn: () => getContactAssignees(localGovernmentId as string),
    enabled: Boolean(localGovernmentId),
    staleTime: 60_000,
  });
}

export function useFileContactHistory(fileId: string) {
  return useQuery({
    queryKey: frontDeskKeys.fileHistory(fileId),
    queryFn: () => getFileContactHistory(fileId),
    // A file at a municipality without the front desk answers `enabled: false`;
    // a 403/404 should not be retried into a spinner.
    retry: false,
  });
}

function useInvalidateFrontDesk() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: frontDeskKeys.all });
}

export function useCreateContactEntry() {
  const invalidate = useInvalidateFrontDesk();
  return useMutation({ mutationFn: createContactEntry, onSuccess: () => invalidate() });
}

export function useAttachContact() {
  const invalidate = useInvalidateFrontDesk();
  return useMutation({
    mutationFn: ({ id, fileId }: { id: string; fileId: string }) => attachContactToFile(id, fileId),
    onSuccess: () => invalidate(),
  });
}

export function useSendContactToTriage() {
  const invalidate = useInvalidateFrontDesk();
  return useMutation({
    mutationFn: ({ id, description }: { id: string; description?: string }) =>
      sendContactToTriage(id, description),
    onSuccess: () => invalidate(),
  });
}

export function useSetContactCallback() {
  const invalidate = useInvalidateFrontDesk();
  return useMutation({
    mutationFn: ({ id, assigneeId, dueAt }: { id: string; assigneeId: string; dueAt: string }) =>
      setContactCallback(id, assigneeId, dueAt),
    onSuccess: () => invalidate(),
  });
}

export function useCompleteCallback() {
  const invalidate = useInvalidateFrontDesk();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) => completeContactCallback(id, note),
    onSuccess: () => invalidate(),
  });
}

/** `datetime-local` input value → ISO string (local time as typed). */
export function localInputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** A sensible default due time for a callback: two hours from now, on the minute. */
export function defaultCallbackDue(now: Date = new Date()): string {
  const d = new Date(now.getTime() + 2 * 60 * 60 * 1000);
  d.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
